import { escapeHtml } from 'shared/utils/sanitizeHtml';

/**
 * Generates a unique GUID for campaign URLs
 */
export function generateCampaignGUID(): string {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

/**
 * Generates a unique campaign URL
 * @param campaignId - The campaign ID (GUID)
 * @param workplaceId - The workplace ID
 * @param baseUrl - The base URL of the application
 */
export function generateCampaignURL(campaignId: string, workplaceId: string, baseUrl: string): string {
	return `${baseUrl}/campaign/respond/${campaignId}/${workplaceId}`;
}

/**
 * Generates a QR code data URL for a campaign
 * @param campaignUrl - The campaign URL to encode
 * @returns Promise<string> - Data URL of the QR code image
 */
export async function generateQRCode(campaignUrl: string): Promise<string> {
	try {
		// Using qrcode library (needs to be installed)
		const QRCode = (await import('qrcode')).default;
		const qrCodeDataURL = await QRCode.toDataURL(campaignUrl, {
			width: 300,
			margin: 2,
			color: {
				dark: '#000000',
				light: '#FFFFFF'
			}
		});
		
		return qrCodeDataURL;
	} catch (error) {
		console.error('Error generating QR code:', error);
		throw new Error('Failed to generate QR code');
	}
}

/**
 * Downloads a QR code as an image file
 * @param qrCodeDataURL - The QR code data URL
 * @param filename - The filename for the downloaded image
 */
export function downloadQRCode(qrCodeDataURL: string, filename: string = 'campaign-qr-code.png'): void {
	const link = document.createElement('a');
	link.href = qrCodeDataURL;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

/**
 * Copies campaign URL to clipboard
 * @param url - The URL to copy
 */
export async function copyURLToClipboard(url: string): Promise<boolean> {
	try {
		await navigator.clipboard.writeText(url);
		return true;
	} catch (error) {
		console.error('Failed to copy URL:', error);
		// Fallback method
		try {
			const textArea = document.createElement('textarea');
			textArea.value = url;
			textArea.style.position = 'fixed';
			textArea.style.left = '-999999px';
			document.body.appendChild(textArea);
			textArea.select();
			const successful = document.execCommand('copy');
			document.body.removeChild(textArea);
			return successful;
		} catch (fallbackError) {
			console.error('Fallback copy failed:', fallbackError);
			return false;
		}
	}
}

/**
 * Prints QR code
 * @param qrCodeDataURL - The QR code data URL
 * @param campaignTitle - The campaign title to display
 */
export function printQRCode(qrCodeDataURL: string, campaignTitle: string, campaignUrl: string): void {
	const printWindow = window.open('', '', 'width=600,height=600');
	
	if (printWindow) {
		const safeTitle = escapeHtml(campaignTitle);
		const safeUrl = escapeHtml(campaignUrl);
		const safeDataUrl = escapeHtml(qrCodeDataURL);
		printWindow.document.write(`
			<!DOCTYPE html>
			<html>
			<head>
				<title>Print QR Code - ${safeTitle}</title>
				<style>
					body {
						font-family: Arial, sans-serif;
						text-align: center;
						padding: 40px;
					}
					h1 {
						font-size: 24px;
						margin-bottom: 20px;
						color: #333;
					}
					img {
						max-width: 300px;
						margin: 20px auto;
						display: block;
					}
					.url {
						font-size: 12px;
						color: #666;
						word-break: break-all;
						margin-top: 20px;
					}
					@media print {
						body {
							padding: 20px;
						}
					}
				</style>
			</head>
			<body>
				<h1>${safeTitle}</h1>
				<p>Scan the QR code to provide feedback</p>
				<img src="${safeDataUrl}" alt="Campaign QR Code" />
				<div class="url">${safeUrl}</div>
			</body>
			</html>
		`);
		
		printWindow.document.close();
		printWindow.focus();
		
		// Wait for image to load before printing
		setTimeout(() => {
			printWindow.print();
			printWindow.close();
		}, 250);
	}
}

/**
 * Validates if a GUID is valid
 * @param guid - The GUID to validate
 */
export function isValidGUID(guid: string): boolean {
	const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
	return guidRegex.test(guid);
}
